import {
  Button,
  ContentVisibilityToggle,
  Typography,
} from 'melp-design/components';
import { Search } from 'melp-design/icons';
import { SystemColors } from 'melp-design/style';
import { Close } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Drawer,
  IconButton,
  InputBase,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAdminUpdateAdmin } from 'store/admin-admins';
import { AdminRole, useUpdateAdmin, useUpdateMe } from 'store/admins';
import { CurrentUser, useSignout } from 'store/auth';
import { ROUTES } from 'config';
import { useMe } from 'state/Administrators';
import { AppLanguage, appLanguages } from 'types/general';
import { StorageKeys } from 'types/LocalStorage';
import { formatCountry, formatLanguage } from 'utils/format';
import { upper } from 'utils/general';
import { usePredefinedToasts } from 'utils/Toast';
import { ReactComponent as Checkmark } from 'assets/icons/checkmark.svg';

const LANGUAGE_PREVIEW_COUNT = 9;
const LANGUAGE_COLUMN_COUNT = 3;
const LANGUAGE_COLLATOR = new Intl.Collator(undefined, {
  sensitivity: 'base',
});

const scriptGroup = (label: string) => {
  for (const char of label) {
    if (/\p{Script=Greek}/u.test(char)) return 1;
    if (/\p{Script=Cyrillic}/u.test(char)) return 2;
  }

  return 0;
};

const compareLanguages = (a: AppLanguage, b: AppLanguage) => {
  const labelA = formatLanguage(a, a);
  const labelB = formatLanguage(b, b);
  const byScript = scriptGroup(labelA) - scriptGroup(labelB);

  if (byScript !== 0) return byScript;

  return LANGUAGE_COLLATOR.compare(labelA, labelB);
};

const sortedAppLanguages = [...appLanguages].sort(compareLanguages);

const ROLE_LABEL: Record<AdminRole, string> = {
  hrAdmin: 'HR admin',
  melpAdmin: 'Melp admin',
  hrProgrammatic: 'HR programmatic',
};

const textValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const initialsFrom = (value: string) => {
  const parts = value.trim().split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return value.trim().slice(0, 2).toUpperCase();
};

const getInitials = (me?: CurrentUser) => {
  const firstName = textValue(me?.firstName);
  const lastName = textValue(me?.lastName);

  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }

  if (firstName || lastName) {
    return initialsFrom(firstName || lastName || '');
  }

  const companyName = textValue(me?.parentCompany?.name);
  if (companyName) return initialsFrom(companyName);

  const email = textValue(me?.email);
  if (email) return initialsFrom(email);

  return '';
};

const avatarSx = (size: number) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: SystemColors.primary.tint,
  color: SystemColors.primary.base,
  fontWeight: 600,
  fontSize: size > 40 ? 18 : 13,
  letterSpacing: '0.02em',
  lineHeight: 1,
  fontFamily: 'Inter, sans-serif',
});

const LanguageOption = ({
  language,
  selected,
  disabled,
  onSelect,
}: {
  language: AppLanguage;
  selected: boolean;
  disabled: boolean;
  onSelect: (language: Uppercase<AppLanguage>) => void;
}) => {
  const label = formatLanguage(language, language);

  return (
    <ButtonBase
      title={label}
      data-test={language}
      disabled={disabled}
      onClick={() => onSelect(upper(language))}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 0.5,
        height: 28,
        px: 1,
        borderRadius: '8px',
        textAlign: 'left',
        backgroundColor: selected ? SystemColors.primary.tint : 'transparent',
        color: selected ? SystemColors.primary.base : SystemColors.black,
        '&:hover': {
          backgroundColor: selected
            ? SystemColors.primary.tint
            : SystemColors.grey[96],
        },
      }}
    >
      <Typography variant="p2" color="inherit" noWrap>
        {label}
      </Typography>
      {selected ? (
        <Checkmark style={{ display: 'block', flexShrink: 0 }} />
      ) : null}
    </ButtonBase>
  );
};

const MetaRow = ({ label, value }: { label: string; value?: string }) => {
  if (!value) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
      }}
    >
      <Typography variant="p2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="p2" sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  );
};

export const AccountSidebar = () => {
  const history = useHistory();
  const { i18n, t } = useTranslation();
  const { me, loading, isMelpAdmin, isHrAdmin } = useMe();
  const predefinedToasts = usePredefinedToasts();
  const [open, setOpen] = useState(false);
  const [languageQuery, setLanguageQuery] = useState('');
  const [showAllLanguages, setShowAllLanguages] = useState(false);

  const { mutate: updateAdmin, isLoading: isUpdating } = useUpdateAdmin();
  const { mutate: updateMe, isLoading: isUpdatingMe } = useUpdateMe();
  const { mutate: updateMelpAdmin, isLoading: isUpdatingMelpAdmin } =
    useAdminUpdateAdmin();
  const { mutate: signout, isLoading: isSigningOut } = useSignout();

  const isChangingLanguage = isUpdating || isUpdatingMe || isUpdatingMelpAdmin;
  const initials = getInitials(me);
  const fullName = [textValue(me?.firstName), textValue(me?.lastName)]
    .filter(Boolean)
    .join(' ');
  const companyName = textValue(me?.parentCompany?.name);
  const email = textValue(me?.email);
  const phone = textValue(me?.phone);
  const heading = fullName || companyName || email;
  const roleName =
    me?.adminRole?.name || (me ? ROLE_LABEL[me.role] : undefined);
  const roleValue = [roleName, me?.root ? 'Root' : undefined]
    .filter(Boolean)
    .join(' · ');
  const scopes = me?.permissionScopes
    .map((scope) => scope.name)
    .filter(Boolean)
    .join(', ');
  const country = me?.parentCompany?.country
    ? formatCountry(i18n.language, me.parentCompany.country)
    : undefined;

  const languages = useMemo(() => {
    const query = languageQuery.trim().toLowerCase();

    return sortedAppLanguages.filter((language) => {
      if (!query) return true;

      const label = formatLanguage(language, language).toLowerCase();
      return label.includes(query) || language.includes(query);
    });
  }, [languageQuery]);

  const currentLanguage = i18n.language.toLowerCase();
  const isLanguageSelected = (language: string) =>
    currentLanguage === language || currentLanguage.startsWith(`${language}-`);
  const canExpandLanguages = languages.length > LANGUAGE_PREVIEW_COUNT;
  const languagesExpanded = showAllLanguages && canExpandLanguages;
  const visibleLanguages = (() => {
    if (!canExpandLanguages || languagesExpanded) return languages;

    const preview = languages.slice(0, LANGUAGE_PREVIEW_COUNT);
    const selectedLanguage = languages.find((language) =>
      isLanguageSelected(language),
    );

    if (!selectedLanguage || preview.includes(selectedLanguage)) {
      return preview;
    }

    return [...preview.slice(0, LANGUAGE_PREVIEW_COUNT - 1), selectedLanguage];
  })();
  const hiddenLanguageCount = languages.length - visibleLanguages.length;
  const languageRowCount = Math.max(
    1,
    Math.ceil(visibleLanguages.length / LANGUAGE_COLUMN_COUNT),
  );

  const handleClose = () => {
    setOpen(false);
    setLanguageQuery('');
    setShowAllLanguages(false);
  };

  const changeLanguage = async (language: Uppercase<AppLanguage>) => {
    await i18n.changeLanguage(language.toLowerCase());
    localStorage.setItem(StorageKeys.lang, language.toLowerCase());

    if (isHrAdmin && me) {
      updateAdmin(
        {
          adminId: me.id,
          data: { language },
        },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
          },
        },
      );
      updateMe({ data: { language } });
    }

    if (me && isMelpAdmin) {
      updateMelpAdmin({ adminId: me.id, data: { language } });
    }
  };

  if (loading && !me) {
    return <CircularProgress size={20} color="primary" />;
  }

  return (
    <>
      <ButtonBase
        onClick={() => setOpen(true)}
        data-test="account"
        aria-label={heading || t('common.name')}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          ...avatarSx(36),
          transition: 'box-shadow 150ms',
          '&:hover': {
            boxShadow: `0 0 0 4px ${SystemColors.primary.tint}`,
          },
          '&:focus-visible': {
            outline: `2px solid ${SystemColors.primary.base}`,
            outlineOffset: 2,
          },
        }}
      >
        {initials}
      </ButtonBase>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 'min(400px, 100vw)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: `1px solid ${SystemColors.grey[90]}`,
            backgroundColor: SystemColors.white,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box sx={avatarSx(56)} aria-hidden>
              {initials}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {heading ? <Typography variant="h2">{heading}</Typography> : null}
              {email && email !== heading ? (
                <Typography
                  variant="p2"
                  color="textSecondary"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {email}
                </Typography>
              ) : null}
              {phone ? (
                <Typography variant="p2" color="textSecondary">
                  {phone}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            aria-label={t('common.close')}
            size="small"
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {companyName || roleValue || country || scopes ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
              }}
            >
              <MetaRow
                label={t('companies.company_name')}
                value={companyName}
              />
              <MetaRow label={t('permissions.role.title')} value={roleValue} />
              <MetaRow label={t('benefits.country')} value={country} />
              <MetaRow label={t('sidebar.companies')} value={scopes} />
            </Box>
          ) : null}

          <Box>
            <Typography variant="h3" sx={{ mb: 1.25 }}>
              {t('form.preferred_language')}
            </Typography>
            <InputBase
              value={languageQuery}
              onChange={({ target }) => setLanguageQuery(target.value)}
              placeholder={t('common.search')}
              startAdornment={
                <Box
                  sx={{
                    display: 'flex',
                    color: SystemColors.grey[45],
                    mr: 1,
                    flexShrink: 0,
                  }}
                >
                  <Search style={{ width: 16, height: 16 }} />
                </Box>
              }
              inputProps={{
                'aria-label': t('form.preferred_language'),
                autoComplete: 'off',
              }}
              sx={{
                width: '100%',
                height: 36,
                px: 1.25,
                mb: 1,
                borderRadius: '8px',
                backgroundColor: SystemColors.grey[96],
                fontSize: 13,
              }}
            />
            <Box
              data-test="language-select"
              sx={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridTemplateColumns: `repeat(${LANGUAGE_COLUMN_COUNT}, minmax(0, 1fr))`,
                gridTemplateRows:
                  languages.length === 0
                    ? 'auto'
                    : `repeat(${languageRowCount}, 28px)`,
                gap: 0.75,
                alignContent: 'start',
              }}
            >
              {languages.length === 0 ? (
                <Box sx={{ gridColumn: '1 / -1', py: 2, textAlign: 'center' }}>
                  <Typography variant="p2" color="textSecondary">
                    {t('errors.nothing_found')}
                  </Typography>
                </Box>
              ) : (
                visibleLanguages.map((language) => (
                  <LanguageOption
                    key={language}
                    language={language}
                    selected={isLanguageSelected(language)}
                    disabled={isChangingLanguage}
                    onSelect={changeLanguage}
                  />
                ))
              )}
            </Box>
            {canExpandLanguages ? (
              <Box sx={{ mt: 1 }}>
                <ContentVisibilityToggle
                  contentVisible={languagesExpanded}
                  onChange={setShowAllLanguages}
                  count={hiddenLanguageCount}
                  fullWidth
                />
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            borderTop: `1px solid ${SystemColors.grey[90]}`,
            backgroundColor: SystemColors.white,
          }}
        >
          <Button
            variant="neutral-outline"
            label={t('actions.change_password')}
            style={{ width: '100%' }}
            onClick={() => {
              handleClose();
              history.push(ROUTES.auth.changePassword);
            }}
          />
          <Button
            variant="danger-outline"
            label={t('common.logout')}
            style={{ width: '100%' }}
            loading={isSigningOut}
            disabled={isSigningOut}
            onClick={() => {
              handleClose();
              signout();
            }}
          />
        </Box>
      </Drawer>
    </>
  );
};
